/**
 * Text editing.
 *
 * A plain `<textarea>`, on purpose. A contenteditable surface would give
 * richer decoration and would also, sooner or later, normalise the user's line
 * endings, swallow a tab, or reorder combining marks in Hangul — and §13
 * forbids altering a document in ways nobody asked for. A textarea returns
 * exactly the string it was given.
 *
 * The gutter mirrors the textarea's scroll rather than sharing a container, so
 * line numbers stay aligned without wrapping the editable region in anything
 * that could interfere with the caret.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withText } from '@moduviewer/handlers';
import type { TextModel } from '@moduviewer/handlers';

export interface TextEditorProps {
  readonly model: TextModel;
  readonly wrap: boolean;
  readonly onChange: (next: TextModel) => void;
}

export function TextEditor({ model, wrap, onChange }: TextEditorProps): React.JSX.Element {
  const area = useRef<HTMLTextAreaElement>(null);
  const gutter = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const lineCount = useMemo(() => model.text.split('\n').length, [model.text]);

  useEffect(() => {
    if (gutter.current) gutter.current.scrollTop = scrollTop;
  }, [scrollTop]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab indents instead of leaving the field. Escape restores tab as a way
    // out, so the editor never becomes a keyboard trap (§26).
    if (e.key !== 'Tab' || e.shiftKey) return;
    e.preventDefault();
    const el = e.currentTarget;
    const { selectionStart, selectionEnd, value } = el;
    const next = `${value.slice(0, selectionStart)}\t${value.slice(selectionEnd)}`;
    onChange(withText(model, next));
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = selectionStart + 1;
    });
  }, [model, onChange]);

  return (
    <div className="text-view" style={{ flex: 1, minHeight: 0 }}>
      {!wrap ? (
        <div className="text-view__gutter" ref={gutter} aria-hidden="true">
          {Array.from({ length: lineCount }, (_, i) => <div key={i}>{i + 1}</div>)}
        </div>
      ) : null}
      <textarea
        ref={area}
        className={`editor${wrap ? ' editor--wrap' : ''}`}
        value={model.text}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        aria-label="문서 내용"
        onChange={(e) => onChange(withText(model, e.target.value))}
        onKeyDown={onKeyDown}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      />
    </div>
  );
}
